import instance from "../auth/authApi";

export type RiskOverrideScope = "global" | "tenant";
export type RiskOverrideStatus = "draft" | "published";
export type RiskOverrideKind = "replace" | "add" | "exclude";

export interface RiskOverrideDTO {
  id: number;
  dataset?: string;
  scope: RiskOverrideScope;
  tenantId: string | null;
  riskLevel: number | null;
  status: RiskOverrideStatus;
  priority: number;
  note: string | null;
  overrideKind: RiskOverrideKind;
  sourceFeatureId: string | null;
  returnPeriodMin?: number | null;
  returnPeriodMax?: number | null;
  geometryGeoJson?: Record<string, unknown>;
}

export interface CreateRiskOverridePayload {
  dataset: string;
  scope: RiskOverrideScope;
  tenantId?: string | null;
  geometry: Record<string, unknown>;
  riskLevel?: number | null;
  status?: RiskOverrideStatus;
  priority?: number;
  note?: string;
  overrideKind?: RiskOverrideKind;
  sourceFeatureId?: string | number | null;
  upsert?: boolean;
  returnPeriodMin?: number | null;
  returnPeriodMax?: number | null;
}

export interface PatchRiskOverridePayload {
  dataset: string;
  geometry?: Record<string, unknown>;
  riskLevel?: number | null;
  status?: RiskOverrideStatus;
  priority?: number;
  note?: string | null;
  overrideKind?: RiskOverrideKind;
  sourceFeatureId?: string | number | null;
  returnPeriodMin?: number | null;
  returnPeriodMax?: number | null;
}

/** Affiche min/max : "" | "10" | "10-30" | "1000+". */
export function formatReturnPeriod(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  if (min == null || !Number.isFinite(min)) {
    return "";
  }
  const a = Math.trunc(min);
  if (max == null || !Number.isFinite(max)) {
    return `${a}+`;
  }
  const b = Math.trunc(max);
  if (a === b) {
    return String(a);
  }
  return `${a}-${b}`;
}

export type ParsedReturnPeriod =
  | { ok: true; min: number | null; max: number | null }
  | { ok: false; error: string };

/**
 * Parse une saisie libre : vide, "10", "10-30", "1000+".
 * Espaces ignorés.
 */
export function parseReturnPeriodInput(raw: string): ParsedReturnPeriod {
  const s = raw.trim().replace(/\s+/g, "");
  if (s === "") {
    return { ok: true, min: null, max: null };
  }
  const plus = /^(\d+)\+$/.exec(s);
  if (plus) {
    const min = Number(plus[1]);
    if (!Number.isFinite(min) || min <= 0) {
      return { ok: false, error: "La période de retour doit être un entier > 0." };
    }
    return { ok: true, min, max: null };
  }
  const range = /^(\d+)-(\d+)$/.exec(s);
  if (range) {
    const min = Number(range[1]);
    const max = Number(range[2]);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= 0) {
      return { ok: false, error: "La période de retour doit être un entier > 0." };
    }
    if (max < min) {
      return { ok: false, error: "La borne haute doit être ≥ la borne basse." };
    }
    return { ok: true, min, max };
  }
  const exact = /^(\d+)$/.exec(s);
  if (exact) {
    const n = Number(exact[1]);
    if (!Number.isFinite(n) || n <= 0) {
      return { ok: false, error: "La période de retour doit être un entier > 0." };
    }
    return { ok: true, min: n, max: n };
  }
  return {
    ok: false,
    error: "Format attendu : 10, 10-30 ou 1000+ (vide = aucune).",
  };
}

export async function createRiskOverride(payload: CreateRiskOverridePayload) {
  const response = await instance.post("/risk/override", payload);
  return response.data as {
    success?: boolean;
    override?: RiskOverrideDTO;
  };
}

export async function patchRiskOverride(id: number, payload: PatchRiskOverridePayload) {
  const response = await instance.patch(`/risk/override/${id}`, payload);
  return response.data as {
    success?: boolean;
    override?: RiskOverrideDTO;
  };
}

export async function listRiskOverrides(params: {
  dataset: string;
  /** Contexte carte : global seul si `""`, sinon global + overrides du tenant. */
  mapTenantId?: string;
  scope?: RiskOverrideScope;
  tenantId?: string | null;
  status?: RiskOverrideStatus;
  limit?: number;
}) {
  const response = await instance.get("/risk/override", { params });
  return response.data as {
    success?: boolean;
    overrides?: RiskOverrideDTO[];
  };
}

export async function getRiskOverride(id: number, dataset: string) {
  const response = await instance.get(`/risk/override/${id}`, {
    params: { dataset },
  });
  return response.data as {
    success?: boolean;
    override?: RiskOverrideDTO;
  };
}

export async function deleteRiskOverride(id: number, dataset: string) {
  const response = await instance.delete(`/risk/override/${id}`, {
    params: { dataset },
  });
  return response.data as { success?: boolean };
}
